/**
 * The GF switcher store — loads the account's companions and exposes them
 * switcher-ready (visible + most-recently-active first) plus a resolver for the
 * active companion given the route. Thin: the edge is the authority on the list
 * and its order; the pure `companions-order` helpers re-derive both defensively.
 */
import type { ApiError } from '@4f/shared';
import { isErr } from '@4f/shared';
import { createMemo, createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { CompanionView } from '../api/types.ts';
import { resolveActive, visibleCompanions } from './companions-order.ts';

/** The reactive surface the switcher + surface shell consume. */
export interface CompanionsStore {
  /** Every companion the account has (raw). */
  readonly all: () => readonly CompanionView[];
  /** Switcher list — `disabled` hidden, most-recent-first. */
  readonly visible: () => readonly CompanionView[];
  readonly loading: () => boolean;
  readonly error: () => ApiError | null;
  /** Resolve the active companion for a requested id (route), with a safe fallback. */
  active(requestedId: string | null): CompanionView | null;
  reload(): Promise<void>;
}

export function createCompanionsStore(api: ApiClient): CompanionsStore {
  const [all, setAll] = createSignal<readonly CompanionView[]>([]);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);

  const visible = createMemo(() => visibleCompanions(all()));

  const reload = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listCompanions();
      if (isErr(result)) {
        setError(result.error);
        return;
      }
      setAll(result.value);
    } finally {
      setLoading(false);
    }
  };

  return {
    all,
    visible,
    loading,
    error,
    active: (requestedId) => resolveActive(all(), requestedId),
    reload,
  };
}
